import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    HistoryDailyCountsQueryObjectSchema,
    HistoryDailyCountsResponseSchema,
    HistoryDetailsErrorSchema,
    HistoryDetailsQueryObjectSchema,
    HistoryDetailsResponseSchema,
    HistoryFilterObjectSchema,
    HistoryQuerySchema,
    HistoryResponseSchema,
} from "@common/contracts/auditLog.contract";
import { refineHistoryCountsRange } from "@common/schemas/domains/activity/activity-timeline.schema";

const c = initContract();

const MAX_ORGANIZATION_AUDIT_LOG_LIMIT = 100;

export const OrganizationHistoryFilterSchema = HistoryFilterObjectSchema.extend(
    {
        clientWorkspaceIds: z.array(z.string().uuid()).optional(),
    },
).refine((data) => !data.showReverted || !data.revertedOnly, {
    message: "showReverted and revertedOnly are mutually exclusive",
});

export const OrganizationHistoryQuerySchema = HistoryQuerySchema.omit({
    relatedResource: true,
}).extend({
    filters: OrganizationHistoryFilterSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(MAX_ORGANIZATION_AUDIT_LOG_LIMIT)
        .default(20),
});

export const OrganizationHistoryDailyCountsQuerySchema =
    HistoryDailyCountsQueryObjectSchema.extend({
        filters: OrganizationHistoryFilterSchema.optional(),
    }).superRefine(refineHistoryCountsRange);

export const OrganizationHistoryDetailsQuerySchema =
    HistoryDetailsQueryObjectSchema.omit({
        grouped: true,
        groupBySourceId: true,
        drillDownResourceType: true,
        drillDownChangeType: true,
        relatedResource: true,
    })
        .extend({
            clientWorkspaceId: z.string().uuid().optional(),
            page: z.coerce.number().int().min(1).default(1),
            limit: z.coerce
                .number()
                .int()
                .min(1)
                .max(MAX_ORGANIZATION_AUDIT_LOG_LIMIT)
                .default(20),
        })
        .refine((data) => !data.showReverted || !data.revertedOnly, {
            message: "showReverted and revertedOnly are mutually exclusive",
        });

export const organizationAuditLogContract = c.router({
    getOrganizationHistory: {
        method: "GET",
        path: "/organization/:organizationId/history",
        pathParams: z.object({
            organizationId: z.string().uuid(),
        }),
        query: OrganizationHistoryQuerySchema,
        responses: {
            200: HistoryResponseSchema,
            400: HistoryDetailsErrorSchema,
            403: ErrorMessageSchema,
            404: ErrorMessageSchema,
        },
    },
    getOrganizationHistoryDailyCounts: {
        method: "GET",
        path: "/organization/:organizationId/history/daily-counts",
        pathParams: z.object({
            organizationId: z.string().uuid(),
        }),
        query: OrganizationHistoryDailyCountsQuerySchema,
        responses: {
            200: HistoryDailyCountsResponseSchema,
            400: HistoryDetailsErrorSchema,
            403: ErrorMessageSchema,
            404: ErrorMessageSchema,
        },
    },
    getOrganizationHistoryDetails: {
        method: "GET",
        path: "/organization/:organizationId/history/details",
        pathParams: z.object({
            organizationId: z.string().uuid(),
        }),
        query: OrganizationHistoryDetailsQuerySchema,
        responses: {
            200: HistoryDetailsResponseSchema,
            400: HistoryDetailsErrorSchema,
            403: ErrorMessageSchema,
            404: ErrorMessageSchema,
        },
    },
});
